import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpRequest } from '@angular/common/http';
import { CoreModule } from './core/core.module';
import { AppComponent } from './app.component';
import {
  DARK_THEME,
  DEFAULT_THEME,
  NbDatepickerModule,
  NbDialogModule,
  NbIconModule,
  NbMenuModule,
  NbSidebarModule,
  NbThemeModule,
  NbTimepickerModule,
  NbToastrModule,
  NbWindowModule,
} from '@nebular/theme';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { NbPasswordAuthStrategy, NbAuthModule, NbAuthJWTInterceptor, NB_AUTH_TOKEN_INTERCEPTOR_FILTER, NbAuthOAuth2JWTToken } from '@nebular/auth';
import { AuthGuardService } from './core/services/auth-guard.service';
import { NgxEchartsModule } from 'ngx-echarts';
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { environment } from '../environments/environment';
import { provideNgxMask } from 'ngx-mask';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import * as echarts from 'echarts/core';
import { BarChart, GraphChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent, DatasetComponent, LegendScrollComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  BarChart, 
  LineChart, 
  PieChart,
  GraphChart,
  GridComponent, 
  TooltipComponent, 
  LegendComponent,
  LegendScrollComponent,
  TitleComponent,
  DatasetComponent,
  CanvasRenderer
]);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    RouterModule,
    BrowserAnimationsModule.withConfig({ disableAnimations: false }),
    NbEvaIconsModule,
    NbIconModule,
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbTimepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    CoreModule.forRoot(),
    NbAuthModule.forRoot({
      strategies: [
        NbPasswordAuthStrategy.setup({
          name: 'email',
          baseEndpoint: environment.mexExchange.api.baseUrl,
          token: {
            class: NbAuthOAuth2JWTToken,
            getter: function (method, response) {
              return {
                access_token: response.body.idToken,
                refresh_token: response.body.refreshToken,
              };
            },
          },
          refreshToken:  {
            endpoint: '/refresh',
            method: 'post',
            requireValidToken: true,
            redirect: {
              success: null,
              failure: null,
            },
            defaultErrors: ['Something went wrong, please try again.'],
            defaultMessages: ['Your token has been successfully refreshed.'],
          },
          login: {
            endpoint: '/login',
            method: 'post',
            redirect: {
              success: '/pages/dashboard',
              failure: null,
            },
            defaultErrors: ['Username/Password combination is not correct, please try again.'],
            defaultMessages: [],
          },
          requestPass: {
            endpoint: '/generate-forgot-password-code-via-email',
            method: 'post',
            redirect: {
              failure: null,
            },
            defaultErrors: ['Username is not correct, please try again.'],
          },
          resetPass: {
            endpoint: '/reset-forgot-password-code-via-email',
            method: 'post',
            redirect: {
              success: '/pages/dashboard',
              failure: null,
            },
            defaultErrors: ['Username is not correct, please try again.'],
          },
          errors: {
            getter: (module, res, options) => {
              return res.error?.errorMessage ? res.error.errorMessage : res.status === 502 || res.status === 0 ? 'The system is temporarily unavailable, Please contact support.' :
                res.status === 405 ? 'Method Not Allowed' : options[module].defaultErrors;
            },
          },
        }),
      ],
      forms: {
        login: {
          redirectDelay: 0,
        },
        requestPass: {
          redirectDelay: 0,
        },
        resetPass: {
          redirectDelay: 0,
        },
      },
    }),
    NgxEchartsModule.forRoot({ echarts }),
    RxReactiveFormsModule
  ],
  providers: [
    AuthGuardService,
    { provide: HTTP_INTERCEPTORS, useClass: NbAuthJWTInterceptor, multi: true },
    { 
      provide: NB_AUTH_TOKEN_INTERCEPTOR_FILTER,
      useValue: (req: HttpRequest<any>) => {
        if (req.url === '/api/login') {
          return true;
        }
        return false;
      },
    },
    ...NbThemeModule.forRoot(
      {
        name: ((localStorage.getItem('admin-theme') === 'dark' || localStorage.getItem('admin-theme') === 'default') ? localStorage.getItem('admin-theme') : 'default')
      },
      [ DEFAULT_THEME, DARK_THEME ],
    ).providers,
    provideNgxMask()
  ],
  bootstrap: [],
})
export class AppModule {}
